/**
 * The authenticator (step 70, S-AUTH-3; backend `modules/auth/mfa.ts`,
 * `http/mfa.ts`). The user, 2026-10-27: TOTP, required at once, for every admin
 * role — platform admins, rave admins, content managers.
 *
 * Inside `RequireApproval`, around the shell: once an account is let in and
 * finished, an admin role's sign-in passes its authenticator before the app.
 * `/me/mfa` is asked once per sign-in; a `mfa_required` refusal from any request
 * asks again (`onMfaRequired`). **`required` decides what is SHOWN, never what
 * is allowed** — the API refuses regardless, and a check that fails (offline)
 * lets the app through, as `CompleteAccount` does.
 *
 * Two screens: set one up (the rule, the QR and the key, a code), or this
 * sign-in's code. No recovery codes (the user, 2026-10-28: "remove the save
 * backup codes") — a lost phone is `pnpm user:mfa-reset`. Sign out is always
 * there — a gate with no way out is a trap.
 *
 * ---
 *
 * **The page is never drawn before the code screen** (the user, 2026-10-28:
 * "first check if he is admin, if yes — straight load the 2fa page … if user
 * is admin and need 2fa you cant load the page").
 *
 * This asked `GET /me/mfa` after booting and rendered `children` while the
 * answer was in flight, so an admin watched the whole console paint and then
 * be replaced by the code screen — the admin console, with its data, on screen
 * for somebody who has not finished signing in.
 *
 * **The check cannot be made on the client.** A rave admin's and a content
 * manager's `User.role` is `USER`; what makes them an admin is a
 * `tenancy.admin` grant (`backend/src/http/helpers.ts`'s `holdsAdmin`). So the
 * SESSION carries the answer — `POST /auth/refresh` and every sign-in answer
 * now hold it (`backend/.../auth/mfaState.ts`), the boot refresh the app
 * already makes — and `sessionMfa()` seeds this query, so the FIRST render is
 * right: an admin who owes a code gets the code screen and nothing else, a
 * raver gets the app with no extra request and no wait.
 *
 * **Where the session said nothing** — an older API, or a borrowed framed
 * session — a platform admin is held on the launch screen until the check
 * answers rather than shown a page, and anybody else goes through as before:
 * a check that fails must never be what stands between a raver and the
 * schedule they saved, which is the rule `CompleteAccount` follows too.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BrandLockup, Button, FormField, LoadingScreen, Stack } from '@/ui';
import { api, ApiError, onMfaRequired, sessionMfa } from '@/lib/api';
import { freshSignIn, lastSignIn, lastSignInHasSecondStep, signedInHere } from '@/lib/signOut';
import { createPasskey, deviceName, passkeysAvailable, type CreationOptionsJSON } from '@/lib/passkey';
import { useT } from '@/i18n';
import { useAuth } from './AuthProvider';
import { QrCode, SecondStep } from './SecondStep';
import { isConsoleHost, isRaveAdminHost } from '@/lib/tenant';
import './RequireMfa.css';

/**
 * Step 90: an admin's second step is a passkey, the authenticator code, or both (the passkey first) —
 * `next` says which this sign-in owes now. A session's seed carries only the first three.
 */
export type MfaState = {
  required: boolean; enrolled: boolean; passed: boolean;
  /** An admin console would ask again now — 10 minutes of an inactive session (2026-11-02). */
  recheck?: boolean;
  method?: 'totp' | 'passkey' | 'both'; next?: 'passkey' | 'totp' | null; codeInstead?: boolean; totpOn?: boolean; totpSince?: string | null;
  passkeys?: { id: string; name: string; createdAt: string; lastUsedAt: string | null }[];
};
const KEY = ['me', 'mfa'] as const;

/**
 * **Whether the account arriving now may owe a second step — known with no network**, before any
 * session has landed (the user, 2026-10-29: "when an admin is logged in, I can still see the app load,
 * then it disappears and asks for the 2FA", and again: "still shows the page before asking the passkey
 * or 2FA"). The boot refresh gives up at 2.5 s — often, just back from Google — and the app opened as
 * the REMEMBERED account (or as nobody), with no session and so no answer; the gate let it through, and
 * the code screen took it away when the session landed.
 *
 * It used to be a per-HOST note of "owed last time", written by this gate — cleared the moment the
 * admin passed, so the NEXT sign-in (a new sign-in always owes again) found nothing; and missing on a
 * host this device had never opened. Now it is the platform cookie every host shares (`lib/signOut.ts`
 * `markSignedIn`'s `.m`), written from the SESSION's own `mfa.required` by every way in (`noteSignIn`):
 *
 *  - this device's signed-in account has a second step → it may owe one now;
 *  - just back from a provider (`freshSignIn`, the API's outcome cookie) → the boot refresh has not
 *    said whose session it is: it may, unless the last account signed in here had none (a raver signing
 *    back in on their own phone does not wait).
 */
function mayOweHere(userId: string | null): boolean {
  if (freshSignIn()) return !lastSignIn() || lastSignInHasSecondStep();
  const here = signedInHere();
  return Boolean(here) && (userId === null || here === userId) && lastSignInHasSecondStep();
}

/** How long an arriving admin waits for the session before the app opens anyway (no locked door). */
export const MFA_WAIT_MS = 10_000;

export function RequireMfa({ children }: { children: ReactNode }) {
  const tr = useT();
  const { user, loading, offline } = useAuth();
  const qc = useQueryClient();
  // The wait for a slow session is capped: a gate must never become a locked door.
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    if (!offline) return undefined;
    const t = setTimeout(() => setWaited(true), MFA_WAIT_MS);
    return () => clearTimeout(t);
  }, [offline]);
  // What the session already said. Seeding the query is what makes the first
  // render right; with it there is no request to wait for at all.
  /*
   * An admin CONSOLE (`radm.`, `appadm.`) asks the server on opening, never the session's seed: after 10 minutes of
   * an inactive session, the passkey or the code again (the user, 2026-11-02 — `recheck`, `backend/.../http/mfa.ts`).
   */
  const consoleHost = isRaveAdminHost() || isConsoleHost();
  const seeded = user && !consoleHost ? sessionMfa(user.id) : null;
  const state = useQuery<MfaState>({
    queryKey: [...KEY, user?.id ?? null],
    queryFn: () => api.get<MfaState>('/me/mfa'),
    enabled: Boolean(user),
    staleTime: Infinity,
    retry: false,
    initialData: seeded ?? undefined,
  });

  useEffect(() => onMfaRequired(() => { void qc.invalidateQueries({ queryKey: KEY }); }), [qc]);

  // The session's answer as soon as it lands — it may arrive after the first render (a slow boot).
  const known: MfaState | undefined = state.data ?? seeded ?? undefined;
  const owesNow = known ? known.required && !known.passed : null;
  // The step passed: every personal read refused while it was owed is asked again (the screens
  // above the gate keep theirs — the menu's My raves said 0 until a reload, the user 2026-10-29).
  const owedBefore = useRef(owesNow);
  useEffect(() => {
    const was = owedBefore.current;
    owedBefore.current = owesNow;
    if (was !== true || owesNow !== false) return;
    void qc.refetchQueries({ predicate: (q) => q.queryKey[0] === 'me' && q.queryKey[1] !== 'mfa' && q.state.status === 'error' });
  }, [owesNow, qc]);

  // Back from the console check: every console read the API refused meanwhile is asked again.
  const recheckNow = Boolean(known?.recheck);
  const recheckBefore = useRef(recheckNow);
  useEffect(() => {
    const was = recheckBefore.current;
    recheckBefore.current = recheckNow;
    if (was && !recheckNow) void qc.refetchQueries({ predicate: (q) => q.state.status === 'error' });
  }, [recheckNow, qc]);

  if (loading) {
    /*
     * **The authenticator is the FIRST check, not one behind the app's loading
     * screen** (the user, 2026-10-28: "the 2fa should come immediately after
     * google without the login screen … it must be first check").
     *
     * A rave host is `RequireSession open` (browsing needs no account), so the
     * app drew at once while the session was still loading and this passed
     * `children` through — the rave's loading screen, then the code screen over
     * it. An admin returning from Google watched the app start before being
     * asked to finish signing in.
     *
     * So when the DEVICE remembers a sign-in (`signedInHere`, a platform cookie
     * read with no network) — or one has just come back from a provider
     * (`freshSignIn`: after a sign-out the marker is older than it, so a fresh
     * Google sign-in used to draw the app here) — the launch screen is held
     * instead: `LoadingScreen` carries `data-boot-wait`, so the splash stays up
     * and the code screen is the first thing drawn. Nobody signed in here waits
     * at all — a rave still opens instantly for a visitor, which is the point of
     * `open`.
     */
    return signedInHere() || freshSignIn() ? <LoadingScreen label={tr('One moment…')} /> : <>{children}</>;
  }
  const s = known;
  if (!s && offline && mayOweHere(user?.id ?? null) && !waited && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
    // An arriving admin whose session has not been heard yet — remembered here or not (a host this
    // device never opened has no remembered account): the launch screen, never the app.
    return <LoadingScreen label={tr('One moment…')} />;
  }
  if (!user) return <>{children}</>;
  if (!s) {
    // Nothing known yet. A platform admin waits on the launch screen
    // (`data-boot-wait`, so the splash holds) rather than being shown a page
    // the gate is about to take away; for anybody else the app opens, and the
    // API refuses whatever this client believes.
    // Past the slow start's wait, the app opens even with the check still in flight (no locked door).
    return (user.role === 'SUPER_ADMIN' || consoleHost || mayOweHere(user.id)) && state.isFetching && !(offline && waited)
      ? <LoadingScreen label={tr('One moment…')} />
      : <>{children}</>;
  }
  const done = () => { void qc.invalidateQueries({ queryKey: KEY }); };
  // Passed, and a console after 10 minutes of an inactive session: the passkey (or the code) again — the same screens.
  if (s.required && s.passed && s.recheck && consoleHost) {
    if (s.method === 'totp' || !passkeysAvailable() || (s.passkeys?.length ?? 1) === 0) return <SecondStep start="code" codeInstead={false} onPassed={done} />;
    return <SecondStep start="passkey" codeInstead={s.totpOn === true} onPassed={done} />;
  }
  if (!s.required || s.passed) return <>{children}</>;
  // A seed from the session says nothing about passkeys: ask before choosing a screen.
  if (!s.enrolled) return <ChooseSecondStep onDone={done} />;
  // The session says which step is owed (step 90); one that does not (an older API) owes the code.
  // Drawn to the Stitch design (`SecondStep.tsx`, 2026-11-02): the passkey, the phone's passkey by MyRave's QR, the code.
  if (s.next !== 'passkey') return <SecondStep start="code" codeInstead={false} onPassed={done} />;
  // The code may stand in for the passkey (the user, 2026-10-30: "default passkey if available, but also let
  // the user use the Google [Authenticator] as a failover, so I can log in on Android"): where no passkey can be
  // used — Android's app — straight to the code; elsewhere the passkey first, the code a tap away. A browser that
  // cannot hold a passkey and has no code still gets the passkey card: its QR uses the phone's (2026-11-02).
  if (s.codeInstead && !passkeysAvailable()) return <SecondStep start="code" codeInstead={false} onPassed={done} />;
  return <SecondStep start="passkey" codeInstead={s.codeInstead === true} onPassed={done} />;
}

/** No second step yet: a passkey or the authenticator code (step 90 — the admin's choice). */
function ChooseSecondStep({ onDone }: { onDone: () => void }) {
  const tr = useT();
  // A browser that cannot hold a passkey has one way: the code, straight away.
  const [way, setWay] = useState<'code' | null>(() => (passkeysAvailable() ? null : 'code'));
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  if (way === 'code') return <SetUp onEnabled={onDone} />;
  async function addPasskey() {
    setBusy(true);
    setError(undefined);
    try {
      await addPasskeyNow();
      onDone();
    } catch (err) {
      setError(passkeyError(err, tr));
      setBusy(false);
    }
  }
  return (
    <Frame title={tr('Choose your second step')} lede={tr('Every MyRave admin finishes signing in with a second step.')}>
      <p className="require-mfa-rule">{tr('All admin users must use two-factor authentication.')}</p>
      <div className="require-mfa-ways">
        {passkeysAvailable() && (
          <button type="button" className="require-mfa-way" onClick={() => void addPasskey()} disabled={busy}>
            <strong>{tr('Passkey')}</strong>
            <span>{tr('Face ID, Touch ID, a fingerprint or a security key — nothing to type.')}</span>
          </button>
        )}
        <button type="button" className="require-mfa-way" onClick={() => setWay('code')} disabled={busy}>
          <strong>{tr('Authenticator code')}</strong>
          <span>{tr('A six-digit code from an authenticator app.')}</span>
        </button>
      </div>
      {error && <p role="alert" className="ui-err">{error}</p>}
    </Frame>
  );
}

/** Register a passkey on this device (the API's options in, its answer out). */
export async function addPasskeyNow(name = deviceName()): Promise<void> {
  const options = await api.post<CreationOptionsJSON>('/me/mfa/passkeys/options', {});
  const response = await createPasskey(options);
  await api.post('/me/mfa/passkeys', { response, name });
}

/** Why a passkey did not go through, in words — a cancelled prompt is not an error of the server's. */
function passkeyError(err: unknown, tr: ReturnType<typeof useT>): string {
  if (err instanceof ApiError) return err.message;
  return tr('The passkey was not used. Try again.');
}

function Frame({ title, lede, children }: { title: string; lede: string; children: ReactNode }) {
  const tr = useT();
  const { signOut } = useAuth();
  return (
    <div className="app-page">
      <header className="app-page-hd"><BrandLockup size={26} href="/" /></header>
      <main className="app-page-centre">
        <Stack gap={14} className="app-narrow require-mfa">
          <h1 className="app-title">{title}</h1>
          <p className="app-lede">{lede}</p>
          {children}
          <Button variant="secondary" onClick={() => void signOut()}>{tr('Sign out')}</Button>
        </Stack>
      </main>
    </div>
  );
}

const messageOf = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

function CodeField({ value, onChange, error }: { value: string; onChange: (v: string) => void; error?: string }) {
  const tr = useT();
  return (
    <FormField label={tr('The code the app shows')} error={error}>
      {(p) => (
        <input
          {...p}
          className="ui-input is-mono require-mfa-code"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
        />
      )}
    </FormField>
  );
}

function SetUp({ onEnabled }: { onEnabled: () => void }) {
  const tr = useT();
  return (
    <Frame
      title={tr('Set up your authenticator')}
      lede={tr('Every MyRave admin signs in with a code from an authenticator app — Google Authenticator, Microsoft Authenticator, 1Password or any other.')}
    >
      <p className="require-mfa-rule">{tr('All admin users must use two-factor authentication.')}</p>
      <AuthenticatorSetUp onEnabled={onEnabled} />
    </Frame>
  );
}

/**
 * The authenticator's set-up itself — the QR code and the key, then a code from the app — with no
 * page around it: the gate's set-up screen above, and the account's Sign-in security card
 * (`MfaSecurity.tsx`) for an admin who signs in with a passkey and adds the code later.
 */
export function AuthenticatorSetUp({ onEnabled }: { onEnabled: () => void }) {
  const tr = useT();
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api.post<{ secret: string; otpauthUrl: string }>('/me/mfa/setup', {})
      .then((s) => { if (live) setSetup(s); })
      .catch((err) => { if (live) setError(messageOf(err, tr('That did not work. Try again.'))); });
    return () => { live = false; };
  }, [tr]);

  async function confirm() {
    setBusy(true);
    setError(undefined);
    try {
      await api.post('/me/mfa/setup/confirm', { code: code.trim() });
      onEnabled();
    } catch (err) {
      setError(messageOf(err, tr('That did not work. Try again.')));
      setBusy(false);
    }
  }

  return setup ? (
    <>
      <p className="app-note">{tr('Scan this with the app, or type the key in it.')}</p>
      <QrCode text={setup.otpauthUrl} label={tr('QR code for your authenticator app')} className="require-mfa-qr" />
      <code className="require-mfa-key" aria-label={tr('Key')}>{setup.secret.match(/.{1,4}/g)?.join(' ')}</code>
      <a className="require-mfa-open" href={setup.otpauthUrl}>{tr('Open in my authenticator app')}</a>
      <form className="require-mfa-form" onSubmit={(e) => { e.preventDefault(); void confirm(); }}>
        <CodeField value={code} onChange={setCode} error={error} />
        <Button type="submit" loading={busy} disabled={code.trim().length < 6}>{tr('Confirm')}</Button>
      </form>
    </>
  ) : error ? <p role="alert" className="ui-err">{error}</p> : <p className="app-note">{tr('One moment…')}</p>;
}
